import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// Lazy wrapper — see components/PlacesAutocompleteLazy (DT-012).
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete'
import PlacesAutocomplete from 'components/PlacesAutocompleteLazy'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import ImagePickerPopup from 'components/popups/ImagePickerPopup'
import { Search, Loader2, MapPin, ImageIcon } from 'lucide-react'
import { createLocation, initializeActiveLocations } from 'slices/locationSlice'
import {
	advanceOnboardingStep,
	setCreatedLocationId,
	ONBOARDING_STEPS,
} from 'slices/onboardingSlice'

interface LocationStepProps {
	user: any
	onSkip: () => void
}

const LOCATION_TYPES = [
	{ value: 'Retail Location', label: 'Retail Location' },
	{ value: 'Retail Center', label: 'Retail Center' },
	{ value: 'Commercial Location', label: 'Commercial Location' },
	{ value: 'Commercial Center', label: 'Commercial Center' },
]

const LocationStep: React.FC<LocationStepProps> = ({ user, onSkip }) => {
	const dispatch = useDispatch()
	const { saving } = useSelector((state: any) => state.onboarding)

	const [address, setAddress] = useState('')
	const [error, setError] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [placeId, setPlaceId] = useState<string | null>(null)
	const [openImagePicker, setOpenImagePicker] = useState(false)

	const [formData, setFormData] = useState({
		assetName: '',
		assetType: 'location',
		locationType: 'Retail Location',
		address: '',
		city: '',
		stateProvince: '',
		postalCode: '',
		country: '',
		latitude: '',
		longitude: '',
		googlePlaceId: null as string | null,
		image: null as string | null,
	})

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
	) => {
		const { name, value } = e.target
		setFormData(prev => ({ ...prev, [name]: value }))
	}

	const handleSelect = async (value: string) => {
		try {
			const results = await geocodeByAddress(value)
			const latLng = await getLatLng(results[0])
			const place = results[0]

			// Capture place_id for Google Places Photos API
			const selectedPlaceId = place.place_id || null
			setPlaceId(selectedPlaceId)

			const newData: any = {
				assetName: value.split(',')[0],
				googlePlaceId: selectedPlaceId,
				address: '',
				postalCode: '',
				city: '',
				stateProvince: '',
				country: '',
				latitude: latLng.lat.toString(),
				longitude: latLng.lng.toString(),
			}

			for (const component of place.address_components) {
				const componentType = component.types[0]
				switch (componentType) {
					case 'street_number':
						newData.address = `${component.long_name} ${newData.address}`
						break
					case 'route':
						newData.address += component.short_name
						break
					case 'postal_code':
						newData.postalCode = `${component.long_name}${newData.postalCode}`
						break
					case 'postal_code_suffix':
						newData.postalCode = `${newData.postalCode}-${component.long_name}`
						break
					case 'locality':
						newData.city = component.long_name
						break
					case 'administrative_area_level_1':
						newData.stateProvince = component.short_name
						break
					case 'country':
						newData.country = component.short_name
						break
				}
			}

			setFormData(prev => ({ ...prev, ...newData }))
			setAddress('')
		} catch (err) {
			setError('Failed to look up address. Please try again.')
		}
	}

	const handleImageSelected = (imageUrl: string) => {
		setFormData(prev => ({ ...prev, image: imageUrl }))
		setOpenImagePicker(false)
	}

	const isValid =
		formData.assetName.trim() !== '' &&
		formData.address.trim() !== '' &&
		formData.city.trim() !== '' &&
		formData.stateProvince.trim() !== '' &&
		formData.postalCode.trim() !== ''

	const hasImage = !!formData.image

	const handleSubmit = async () => {
		if (!isValid) return

		setError('')
		setIsSubmitting(true)

		try {
			const newLocation: any = {
				...formData,
				owner: user._id,
				updateNote: 'New Location',
			}

			if (formData.locationType) {
				const locationType = formData.locationType.toLowerCase()
				const parts = locationType.split(' ')
				if (parts[1]) newLocation.assetType = parts[1]
			}

			const result: any = await dispatch(createLocation({ newLocation }))
			const locationId = result?.payload?._id

			if (locationId) {
				dispatch(setCreatedLocationId(locationId))
			}

			dispatch(initializeActiveLocations())
			dispatch(
				advanceOnboardingStep({
					nextStep: ONBOARDING_STEPS.PROJECT,
					userId: user._id,
				}),
			)
		} catch (err) {
			setError('Failed to create location. Please try again.')
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleBack = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.PROFILE,
				userId: user._id,
			}),
		)
	}

	const handleSkipToInvite = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.INVITE,
				userId: user._id,
			}),
		)
	}

	return (
		<div className="p-6">
			<div className="flex items-center gap-2 mb-1">
				<MapPin className="w-5 h-5 text-[#0080fe]" />
				<h2 className="text-lg font-semibold text-[#074f9b]">
					Create Your First Location
				</h2>
			</div>
			<p className="text-sm text-gray-500 mb-4">
				Locations are the focal point for your deals. Search for a property to
				get started.
			</p>

			{error && (
				<Alert variant="danger" className="mb-4">
					{error}
				</Alert>
			)}

			{/* Google Places Search */}
			<div className="mb-4">
				<Label>Location Search</Label>
				<PlacesAutocomplete
					value={address}
					onChange={setAddress}
					onSelect={handleSelect}
				>
					{({
						getInputProps,
						suggestions,
						getSuggestionItemProps,
						loading,
					}) => (
						<div className="relative">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									{...getInputProps()}
									placeholder="Search by name, address, or zip code"
									className="h-9 pl-9"
								/>
							</div>
							{(loading || suggestions.length > 0) && (
								<div className="absolute left-0 right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-48 overflow-y-auto">
									{loading && (
										<div className="px-3 py-2 text-sm text-gray-500">
											Loading...
										</div>
									)}
									{suggestions.map((suggestion, i) => (
										<div
											key={i}
											{...getSuggestionItemProps(suggestion)}
											className={`px-3 py-2 text-sm cursor-pointer ${
												suggestion.active
													? 'bg-[#0080fe]/10 text-[#0080fe]'
													: 'hover:bg-gray-50'
											}`}
										>
											{suggestion.description}
										</div>
									))}
								</div>
							)}
						</div>
					)}
				</PlacesAutocomplete>
			</div>

			{/* Location form fields */}
			<div className="grid grid-cols-2 gap-3 mb-4">
				<div className="col-span-2 grid grid-cols-2 gap-3">
					<div className="space-y-1">
						<Label htmlFor="assetName">Location Name</Label>
						<Input
							id="assetName"
							name="assetName"
							value={formData.assetName}
							onChange={handleChange}
							className="h-8 text-sm"
						/>
					</div>
					<div className="space-y-1">
						<Label htmlFor="locationType">Type</Label>
						<select
							id="locationType"
							name="locationType"
							title="Location Type"
							aria-label="Location Type"
							value={formData.locationType}
							onChange={handleChange}
							className="flex h-8 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							{LOCATION_TYPES.map(type => (
								<option key={type.value} value={type.value}>
									{type.label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="col-span-2 space-y-1">
					<Label htmlFor="loc-address">Address</Label>
					<Input
						id="loc-address"
						name="address"
						value={formData.address}
						onChange={handleChange}
						className="h-8 text-sm"
					/>
				</div>
				<div className="space-y-1">
					<Label htmlFor="city">City</Label>
					<Input
						id="city"
						name="city"
						value={formData.city}
						onChange={handleChange}
						className="h-8 text-sm"
					/>
				</div>
				<div className="space-y-1">
					<Label htmlFor="stateProvince">State/Province</Label>
					<Input
						id="stateProvince"
						name="stateProvince"
						value={formData.stateProvince}
						onChange={handleChange}
						className="h-8 text-sm"
					/>
				</div>
				<div className="space-y-1">
					<Label htmlFor="postalCode">Postal Code</Label>
					<Input
						id="postalCode"
						name="postalCode"
						value={formData.postalCode}
						onChange={handleChange}
						className="h-8 text-sm"
					/>
				</div>
				<div className="space-y-1">
					<Label htmlFor="country">Country</Label>
					<Input
						id="country"
						name="country"
						value={formData.country}
						onChange={handleChange}
						className="h-8 text-sm"
					/>
				</div>
			</div>

			{/* Image section — show after address is filled */}
			{isValid && (
				<div className="mb-4">
					{formData.image ? (
						<div className="flex flex-col items-center gap-2">
							<img
								src={formData.image}
								alt={formData.assetName}
								className="max-h-[180px] max-w-full object-contain rounded-lg border border-gray-200"
							/>
							<Button
								type="button"
								variant="ghost"
								onClick={() => setOpenImagePicker(true)}
								className="text-xs"
							>
								Change Image
							</Button>
						</div>
					) : (
						<div className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg">
							<ImageIcon className="w-8 h-8 text-gray-300" />
							<p className="text-sm text-gray-500">
								Add an image to represent this location
							</p>
							<Button
								type="button"
								variant="text"
								onClick={() => setOpenImagePicker(true)}
							>
								<ImageIcon className="w-4 h-4" />
								Select Image
							</Button>
						</div>
					)}
				</div>
			)}

			<div className="flex gap-2">
				<Button
					variant="ghost"
					onClick={handleBack}
					disabled={saving || isSubmitting}
					className="flex-1"
				>
					Back
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={!isValid || !hasImage || saving || isSubmitting}
					className="flex-1"
				>
					{isSubmitting ? (
						<>
							<Loader2 className="w-4 h-4 animate-spin" />
							Creating...
						</>
					) : (
						'Create Location'
					)}
				</Button>
			</div>
			<div className="flex justify-center mt-2">
				<Button
					variant="ghost"
					onClick={handleSkipToInvite}
					disabled={saving}
					className="text-xs text-gray-400"
				>
					Skip
				</Button>
			</div>

			{/* Image Picker Dialog — reuses the same component as Add Location */}
			<Dialog
				open={openImagePicker}
				onClose={() => setOpenImagePicker(false)}
			>
				<DialogContent className="max-w-[700px]">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<ImageIcon className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Select Image</DialogTitle>
								<DialogDescription>
									Choose an image for "{formData.assetName}"
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<ImagePickerPopup
							placeId={placeId}
							searchQuery={
								!placeId && formData.assetName
									? `${formData.assetName} ${formData.city} ${formData.stateProvince}`
									: undefined
							}
							locationName={formData.assetName}
							onImageSelected={handleImageSelected}
						/>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default LocationStep
